import { get } from '@ember/object'
import { isPresent } from '@ember/utils'
import { dasherize } from '@ember/string'
import type { IntlService } from 'ember-intl'
import type { SafeString } from '@ember/template'
import type { ValidateCsvResponse, ValidateCsvSuccessResponse } from 'teacher-ui-client/services/student-import'

/**
 * @param {String} sourcePointer e.g. '/data/1/attributes/teacher-email'
 * @returns {Object} containing attr, value and label properties.
 * @example calling
 * getAttributeForPointer('/data/1/attributes/teacher-email', response.body, intl)
 * // would return
 * {
 *   attr: 'teacher-email',
 *   value: 'x@yields.com',
 *   label: 'Teacher email',
 * }
 */
export function getAttributeForPointer(sourcePointer: string, responseBody: ValidateCsvResponse, intl: IntlService) {
  const sourceSegments = sourcePointer.split('/').filter(isPresent)
  const normalizedSourceSegments = sourceSegments.map(dasherize)
  const sourcePath = normalizedSourceSegments.join('.')
  const value = get(responseBody, sourcePath)
  const attr = normalizedSourceSegments.pop()
  if (attr === undefined) {
    throw new Error('unexpected undefined attribute')
  }
  // we might get back an attribute with a row number as the key
  // e.g. /attribute/4
  // rename the attribute to the candidate student attribute 'csv-row'
  const attrAsNumber = Number.parseInt(attr, 10)
  if (Number.isInteger(attrAsNumber))
    return {
      attr: 'csv-row',
      value: attrAsNumber,
      label: intl.t('manage.studentCsv.attribute.csv-row'),
    }
  const label = intl.t(`manage.studentCsv.attribute.${attr}`)
  return {
    attr,
    value,
    label,
  }
}

export interface StudentImportError {
  code: string
  csvRow?: number
  detail: string | SafeString
  attribute: { attr: string | undefined; value: unknown; label: string }
  source?: {
    pointer: string
  }
  meta?: {
    value: unknown
    csv_row: number
  }
}

/**
 * Maps a JSONAPI error to a csv row error that the template can display as a row in the error table.
 * - Uses the "error.code" argument to find a translation under "manage.studentCsv.errors.*"
 * - Uses the "source.pointer" argument to find the csv column name.
 * - Falls back to the "manage.studentCsv.defaultRowError" translation.
 * - Passes "error.attr" and "error.value" arguments to the translation as "attr" and "value"
 *   in case they are needed to explain the error.
 * @param {Object} error the JSONAPI error object
 * @param {Object} responseBody the body of the 422 response from gravity
 * @param {IntlService} intl
 * @returns {Object}
 */
export function mapJsonApiErrorToRowError(
  error: StudentImportError,
  responseBody: ValidateCsvResponse,
  intl: IntlService,
) {
  error.csvRow = extractCsvRow(error)
  const sourcePointer = error.source?.pointer
  if (sourcePointer) {
    error.attribute = getAttributeForPointer(sourcePointer, responseBody, intl)
  }
  if (error.code === 'malformed_row' && error.detail) {
    // for malformed_row, gravity provides a better explanation
    // than the FE can provide, so hand the error over for display.
    return error
  }
  if (error.code) {
    const errorValue = error.attribute?.value ?? error.meta?.value
    if (intl.exists(`manage.studentCsv.errors.${error.code}`)) {
      error.detail = intl.t(`manage.studentCsv.errors.${error.code}`, {
        htmlSafe: true,
        attr: error.attribute?.label ?? intl.t('manage.studentCsv.errors.defaultAttributeName'),
        value:
          typeof errorValue === 'string' || typeof errorValue === 'number' || typeof errorValue === 'boolean'
            ? errorValue
            : '',
      })
    } else {
      error.detail = intl.t('manage.studentCsv.errors.default', {
        htmlSafe: true,
        attr: error.attribute?.label ?? intl.t('manage.studentCsv.errors.defaultAttributeName'),
        value:
          typeof errorValue === 'string' || typeof errorValue === 'number' || typeof errorValue === 'boolean'
            ? errorValue
            : '',
      })
    }
  }
  return error
}

/**
 * @param {Object} error
 * @returns {Number} the csv row starting at 1
 */
export function extractCsvRow(error: StudentImportError) {
  return error.meta?.csv_row
}

/**
 * Converts the validate csv endpoint's response from a JSONAPI doc to an array of POJO objects
 * representing students (and their class and teacher) that will be created should the import be
 * confirmed.
 */
export function normalizeValidateCsvResponse(payload: ValidateCsvSuccessResponse) {
  return payload.data.map((student, index) => {
    const { attributes } = student
    let csvRow = parseInt(attributes['csv-row'], 10)
    if (Number.isNaN(csvRow)) {
      csvRow = index + 1
    }
    return {
      firstName: attributes['first-name'],
      lastName: attributes['last-name'],
      gradePosition: attributes['grade-position'],
      externalId: attributes['external-id'],
      csvRow,
      duplicateSource: attributes['duplicate-source'] ?? false,
      duplicateDestination: attributes['duplicate-destination'] ?? false,
      studentNameChange: attributes['student-name-change'] ?? false,
      teacherEmail: attributes['teacher-email'],
      schoolClassName: attributes['school-class-name'],
    }
  })
}

export type CandidateStudents = ReturnType<typeof normalizeValidateCsvResponse>
